import React, {
    Children,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { JOB_MANAGER_PATHS } from 'constants/job-manager-paths';
import { LandingPageWrapper } from 'components';
import { ChainSvg } from '../../assets';
import InvalidPermissionsDialog from '../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { useAuth, usePermission } from '../../core';
import { ROLES } from '../../core/contexts/permissions';
import { useDownloadJob } from './components/download-job-menu/useDownloadJob';
import JobManagerDetailsDrawer from './components/job-manager-details/JobManagerDetailsDrawer';
import {
    getColumns,
    JobRowData,
} from './components/job-manager-list/job-list-helpers';
import {
    getJobDetailsType,
    JobSummary,
    LIMIT,
    toLocalDateTimeString,
} from 'lib';
import { useJobList, useUserList } from 'hooks';
import {
    defaultJobManagerQueryPropsModel,
    defaultJobSummary,
    downloadData,
    JobManagerQueryPropsModel,
    noOfDays,
    selectFieldStyles,
    TotalJobSummary,
} from './job-manager-helper';
import { MenuItem } from '@biq/apex-ui';
import {
    GridFilterModel,
    GridState,
    gridVisibleRowsLookupSelector,
} from '@mui/x-data-grid';

export const JobManager = (): React.JSX.Element => {
    const { getRoles, hasRequiredRoles } = useAuth();
    const isAdminRole: boolean = getRoles().includes(ROLES.admin);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const navigate = useNavigate();
    const { hasPermissions } = usePermission();
    const [selectedJobMenuId, setSelectedJobMenuId] = useState<
        string | undefined
    >(undefined);
    const [downloadJobId, setDownloadJobId] = useState<string | undefined>(
        undefined
    );
    const [downloadFileType, setDownloadFileType] = useState<
        string | undefined
    >(undefined);
    const [selectedJob, setSelectedJob] = useState<JobRowData | undefined>(
        undefined
    );
    const [totalJobSummary, setTotalJobSummary] =
        useState<TotalJobSummary>(defaultJobSummary);
    const [selectedNoOfDays, setSelectedNoOfDays] = useState<number>(5);
    const [filteredJobsData, setFilteredJobsData] = useState<JobRowData[]>([]);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [queryPropsModel, setQueryPropsModel] =
        useState<JobManagerQueryPropsModel>(defaultJobManagerQueryPropsModel);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
    });
    const [rowFilteredCount, setRowFilteredCount] = useState<number>();

    const isFilterApplied = filterModel.items.length > 0;

    const {
        data: summaryData,
        isLoading: isSummaryDataLoading,
        isFetched: isSummaryDataFetched,
        refetch: refreshJobList,
        isRefetching: isSummaryDataRefetching,
        isSuccess: isSummaryDataSuccess,
    } = useJobList({
        limit: queryPropsModel.limit,
        skip: queryPropsModel.skip,
        days: queryPropsModel.days,
    });
    const { data: userList } = useUserList();

    const {
        refetch: downloadJob,
        isFetched: isDownloadJobFetched,
        isSuccess: isDownloadJobSuccess,
        data: downloadedJobData,
    } = useDownloadJob({ jobId: downloadJobId, fileType: downloadFileType });

    useEffect(() => {
        if (isDownloadJobFetched && isDownloadJobSuccess && downloadedJobData) {
            setDownloadJobId(undefined);
            setDownloadFileType(undefined);
            downloadData(downloadedJobData);
            setTimeout(() => {
                refreshJobList();
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadedJobData, isDownloadJobFetched, isDownloadJobSuccess]);

    useEffect(() => {
        if (downloadJobId) {
            downloadJob();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadJobId]);

    useEffect(() => {
        setIsDataLoading(true);
        if (
            summaryData &&
            isSummaryDataFetched &&
            isSummaryDataSuccess &&
            !isSummaryDataRefetching
        ) {
            const summaryDataResults = summaryData?.data?.jobs ?? [];
            const isDataFirstPage =
                queryPropsModel.limit === LIMIT && queryPropsModel.skip === 0;
            // possibly remove any duplicate entries we get from fetching the next 100 records
            const currentDataSet = new Set([
                ...totalJobSummary.list,
                ...summaryDataResults,
            ]);
            // if is the first page, set the total data, otherwise, concatenate the new data to previous jobSummaryList and remove duplicates via Set.
            const newSummaryData = isDataFirstPage
                ? summaryDataResults
                : Array.from(currentDataSet);
            const totalJobs =
                summaryData?.data?.total ?? summaryDataResults.length;

            setTotalJobSummary({
                list: newSummaryData,
                total: totalJobs,
            });
            setTimeout(() => {
                setIsDataLoading(false);
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        summaryData,
        isSummaryDataFetched,
        isSummaryDataSuccess,
        isSummaryDataRefetching,
        queryPropsModel.limit,
        queryPropsModel.skip,
    ]);

    useEffect(() => {
        refreshJobList();
    }, [queryPropsModel, refreshJobList]);

    const downloadSelectedJob = (id: string | undefined): void => {
        setDownloadJobId(id);
    };

    const hasAllowedFileDownloadRoles = (): boolean => {
        return hasRequiredRoles(['sdzReadOnly', 'sdzUser']);
    };

    const handleMenuJobOpen = (jobId: string): void => {
        setSelectedJobMenuId(jobId);

        // Only check the download fileType when the user opens the current job menu.
        // Make sure it doesn't override the fileType with undefined when closing the menu.
        if (jobId) {
            const jobWithOpenMenu = (totalJobSummary.list ?? []).find(
                (job: JobSummary) => job.jobId === jobId
            );
            setDownloadFileType(jobWithOpenMenu?.fileType);
        }
    };

    const cols = getColumns(
        selectedJobMenuId,
        handleMenuJobOpen,
        downloadSelectedJob,
        hasAllowedFileDownloadRoles(),
        refreshJobList
    );

    const findFullNameById = (id: string): string => {
        const user = userList?.data?.find(
            (item: { id: string; email: string; fullName: string }) =>
                item.id === id
        );
        return user ? user.fullName : '';
    };

    const jobsData: JobRowData[] = (totalJobSummary.list ?? []).map(
        (row: JobSummary, index: number) => ({
            ...row,
            formattedJobCreated: toLocalDateTimeString(row.jobCreated, true),
            formattedJobCompleted: toLocalDateTimeString(
                row.jobCompleted,
                true
            ),
            loggedInId: findFullNameById(row.loggedInId),
            dictName: row?.dictName ?? '',
            id: index + 1,
            statusProps: {
                status: row.status,
                jobId: row.jobId,
                jobType: row.name,
                results: row.results,
            },
            formattedJobType: getJobDetailsType(row.name),
        })
    );

    const filterJobData = useCallback(
        (initialJobsData: JobRowData[]): JobRowData[] => {
            const currentDate = new Date();
            const cutoffDate = new Date(
                currentDate.getTime() - selectedNoOfDays * 24 * 60 * 60 * 1000
            );
            return initialJobsData.filter((job) => {
                const jobDate = new Date(job.jobCreated);
                return jobDate >= cutoffDate;
            });
        },
        [selectedNoOfDays]
    );

    useEffect(() => {
        setFilteredJobsData(filterJobData(jobsData));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalJobSummary, selectedNoOfDays]);

    const filterDisplayOnlyProperties = (job: JobRowData): any => {
        // These are the display-only properties added for the main landing table.
        // These formatted properties should not be part of the job manager details drawer.
        const nonAllowedDisplayOnlyProperties = [
            'formattedJobCreated',
            'formattedJobCompleted',
            'formattedJobType',
        ];

        return Object.fromEntries(
            Object.entries(job).filter(
                ([key]) => !nonAllowedDisplayOnlyProperties.includes(key)
            )
        );
    };

    const handleJobClick = (job: JobRowData): void => {
        // If there's a job download menu open, prevent the user from opening the current details
        // drawer and close the current job download menu since user clicked away from menu.
        if (selectedJobMenuId) {
            setSelectedJobMenuId(undefined);
            return;
        }

        const filteredJob = filterDisplayOnlyProperties(job);
        setSelectedJob(filteredJob);
    };

    const handleDrawerClose = (): void => {
        setSelectedJob(undefined);
    };

    const onNewButtonClick = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            navigate(JOB_MANAGER_PATHS.CREATE_NEW_JOB);
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleSelectFilterChange = (filterValue: number): void => {
        setSelectedNoOfDays(filterValue);

        // reset the query props model pages when switching between filters.
        setQueryPropsModel({
            days: filterValue,
            limit: LIMIT,
            skip: 0,
        });
    };

    const handlePaginationModelChanged = (value: any): void => {
        const currentPageSize = paginationModel.pageSize;

        if (value.pageSize !== currentPageSize) {
            // if the pageSize has changed reset the current page to zero.
            setPaginationModel({
                page: 0,
                pageSize: value.pageSize,
            });
        } else {
            // if the page has changed, determine whether we need to fetch more data based on the current items shown and the total items.
            const currentDisplayedItems = value.page * currentPageSize;
            const hasMoreItems =
                totalJobSummary.total > totalJobSummary.list.length;
            const updateData =
                currentDisplayedItems >= totalJobSummary.list.length &&
                hasMoreItems;

            if (updateData) {
                // Fetch 100 items every time
                setQueryPropsModel((prevModel: JobManagerQueryPropsModel) => {
                    return {
                        ...prevModel,
                        limit: prevModel.limit + LIMIT,
                        skip: prevModel.skip + LIMIT,
                    };
                });
            }
            setPaginationModel((prevPaginationModel) => {
                return {
                    ...prevPaginationModel,
                    page: value.page,
                };
            });
        }
    };

    const handleStateChange = (state: GridState): void => {
        const visibleRowsLookup = gridVisibleRowsLookupSelector(state);
        const filtered = filteredJobsData.filter(
            (row: any) => visibleRowsLookup[row.id] !== false
        );
        setRowFilteredCount(filtered.length);
    };

    const memoizedLoading: boolean = useMemo(
        () => isDataLoading || isSummaryDataLoading || isSummaryDataRefetching,
        [isDataLoading, isSummaryDataLoading, isSummaryDataRefetching]
    );

    return (
        <>
            <LandingPageWrapper
                title={'Job Manager'}
                showAddButton={true}
                onAddButtonClick={onNewButtonClick}
                showRefreshButton={true}
                onRefreshButtonClick={refreshJobList}
                tableData={filteredJobsData}
                isLoading={memoizedLoading}
                columns={cols}
                getRowId={(row: any): string => row.id.toString()}
                onRowClick={(dataGridProMap: any): void => {
                    handleJobClick(dataGridProMap?.row);
                }}
                rowCount={
                    isFilterApplied ? rowFilteredCount : totalJobSummary.total
                }
                handleStateChange={handleStateChange}
                filterModel={filterModel}
                onFilterModelChange={(model: GridFilterModel): void =>
                    setFilterModel(model)
                }
                paginationPageSizeOptions={[25, 50, 100]}
                paginationPageSize={25}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChanged}
                emptyStateIcon={ChainSvg}
                emptyStateBodyMessage={
                    'Start a new job using the button at the top right of this page.'
                }
                isSelectFilterVisible={true}
                selectFilterValue={selectedNoOfDays}
                selectFilterStyles={selectFieldStyles}
                selectFilterChildren={Children.toArray(
                    noOfDays.map((item) => (
                        <MenuItem
                            value={item.value}
                            onClick={(): void =>
                                handleSelectFilterChange(item.value)
                            }
                        >
                            {item.label}
                        </MenuItem>
                    ))
                )}
            />
            {selectedJob && (
                <JobManagerDetailsDrawer
                    onDrawerClose={handleDrawerClose}
                    job={selectedJob}
                    receiptType={isAdminRole ? 'admin' : 'user'}
                />
            )}
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </>
    );
};
